import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, FileDiff, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Button } from "./ui/button";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Blok 3, veilig meeprogrammeren stap 1 (Frank, 28 oktober 2026; besluit: bevestigen per bestand).
 *
 * De agent schrijft in een schaduw naast de werkruimte (`services/codevoorstel.py`). Hier staat per
 * bestand wat hij voorstelt, als diff, met Toepassen en Weigeren. Pas na Toepassen verandert de
 * werkruimte. Terugdraaien zet de werkruimte terug naar de staat van voor de laatste agentrit.
 *
 * `versie` laat de ouder een nieuwe lezing vragen (na een agentrit); `onGewijzigd` meldt dat de
 * bestanden in de werkruimte veranderd zijn.
 */
export function diffRegelSoort(regel) {
  if (regel.startsWith("+++") || regel.startsWith("---")) return "kop";
  if (regel.startsWith("@@")) return "blok";
  if (regel.startsWith("+")) return "erbij";
  if (regel.startsWith("-")) return "eraf";
  return "gelijk";
}

const KLEUR = {
  kop: "text-muted-foreground",
  blok: "text-sky-600 dark:text-sky-400",
  erbij: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  eraf: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  gelijk: "",
};

export default function CodeVoorstel({ workspaceId, versie = 0, onGewijzigd }) {
  const { t } = useTaal();
  const [bestanden, setBestanden] = useState([]);
  const [rit, setRit] = useState(null);
  const [bezig, setBezig] = useState("");
  // CA6 (besluit Frank 1 nov): een eigen vastlegboodschap bij Toepassen; leeg geeft "toegepast uit het voorstel: <pad>".
  const [boodschap, setBoodschap] = useState("");

  const laad = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workspaces/${workspaceId}/voorstel`);
      setBestanden(data.bestanden || []);
      setRit(data.rit || null);
    } catch {
      setBestanden([]);
    }
  }, [workspaceId]);

  useEffect(() => {
    laad();
  }, [laad, versie]);

  async function doe(soort, pad) {
    setBezig(`${soort}:${pad}`);
    try {
      const { data } = await axios.post(`${API}/workspaces/${workspaceId}/voorstel/${soort}`,
        soort === "toepassen" && boodschap.trim() ? { path: pad, boodschap: boodschap.trim() } : { path: pad });
      toast.success(soort === "toepassen" ? t("Toegepast: {pad}", { pad }) : t("Geweigerd: {pad}", { pad }));
      // CA8: na het laatste bestand van een issue-rit opent de pull request vanzelf (of staat er waarom niet).
      // EG8: bij een gewone rit in een repo ook; stond er al een open PR van die tak, dan draagt die het werk.
      const pr = data?.issue_pull;
      if (pr?.pull && pr.bestond) toast.success(t("Het werk staat in de open pull request #{n}: {titel}. Samenvoegen doe jij, in Repo's.", { n: pr.pull, titel: pr.titel }));
      else if (pr?.pull) toast.success(t("Pull request #{n} geopend: {titel}. Samenvoegen doe jij, in Repo's.", { n: pr.pull, titel: pr.titel }));
      else if (pr?.geen_pull) toast.error(t(pr.geen_pull));
      await laad();
      if (soort === "toepassen") onGewijzigd?.(pad);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("Dat lukte niet."));
    } finally {
      setBezig("");
    }
  }

  async function terugdraaien() {
    setBezig("terug");
    try {
      await axios.post(`${API}/workspaces/${workspaceId}/terugdraaien`);
      toast.success(t("De werkruimte staat weer zoals voor de agentrit."));
      await laad();
      onGewijzigd?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("Terugdraaien lukte niet."));
    } finally {
      setBezig("");
    }
  }

  if (!bestanden.length && !rit) return null;
  return (
    <section data-testid="code-voorstel" className="space-y-2">
      <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t("Voorstel van de agent")}
      </h2>
      {bestanden.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="voorstel-leeg">
          {t("Er staat niets meer open in het voorstel.")}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {t("De werkruimte verandert pas als je een bestand toepast.")}
          </p>
          <input value={boodschap} onChange={(e) => setBoodschap(e.target.value)} maxLength={500}
                 placeholder={t("Vastlegboodschap bij Toepassen (mag leeg)")} aria-label={t("Vastlegboodschap bij Toepassen (mag leeg)")}
                 className="w-full rounded-md border bg-transparent px-2 py-1 text-xs" data-testid="voorstel-boodschap" />
        </>
      )}
      {bestanden.map((b) => (
        <div key={b.path} className="rounded-md border" data-testid="voorstel-bestand" data-pad={b.path}>
          <div className="flex flex-wrap items-center gap-2 border-b px-2 py-1.5">
            <FileDiff className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-mono text-xs">{b.path}</span>
            <span className="text-[11px] text-muted-foreground">{t(b.soort === "nieuw" ? "nieuw" : b.soort === "verwijderd" ? "verwijderd" : "gewijzigd")}</span>
            <div className="ml-auto flex gap-1.5">
              <Button size="sm" variant="outline" className="h-7 gap-1" disabled={!!bezig}
                      data-testid="voorstel-weigeren" onClick={() => doe("weigeren", b.path)}>
                <X className="h-3.5 w-3.5" />{t("Weigeren")}
              </Button>
              <Button size="sm" className="h-7 gap-1" disabled={!!bezig}
                      data-testid="voorstel-toepassen" onClick={() => doe("toepassen", b.path)}>
                <Check className="h-3.5 w-3.5" />{t("Toepassen")}
              </Button>
            </div>
          </div>
          <pre className="max-h-80 overflow-auto px-2 py-1.5 text-[11px] leading-snug">
            {b.diff.split("\n").map((regel, i) => (
              <div key={i} className={KLEUR[diffRegelSoort(regel)]}>{regel || " "}</div>
            ))}
          </pre>
          {b.afgekapt && (
            <p className="border-t px-2 py-1 text-[11px] text-muted-foreground">
              {t("De diff is ingekort; toepassen neemt het hele bestand over.")}
            </p>
          )}
        </div>
      ))}
      {rit && (
        <Button size="sm" variant="outline" className="h-7 gap-1" disabled={!!bezig}
                data-testid="voorstel-terugdraaien" onClick={terugdraaien}>
          <RotateCcw className="h-3.5 w-3.5" />{t("Terugdraaien naar voor de agentrit")}
        </Button>
      )}
    </section>
  );
}
